/* =============================================================================
   The design system's own strings, in English
   -----------------------------------------------------------------------------
   This catalogue is the BASE every consumer's is merged over. It exists so a
   story, a showcase page or an app that never mounts a `LocaleProvider` still
   renders words rather than a key or a blank — the library is usable on its own,
   and a missing provider degrades to English instead of to nothing.

   Keys are `ds.`-prefixed because the design system cannot know the application's
   namespaces, and an unpredicted collision in a merged catalogue is silent: the
   consumer's entry wins, its own string disappears, and the only symptom is a
   stray English phrase on one screen.

   Only strings the library itself RENDERS belong here. A heading, a column name
   or an empty-state message that arrives as a prop is the caller's to translate.
   ============================================================================= */

export const en = {
  /** The brand link's accessible name. The product name itself is not translated. */
  'ds.header.home': 'escrowbuild.io home',
  /** The menu's own accessible name, and the prefix of the trigger's. */
  'ds.header.language': 'Language',
  'ds.header.languageNamed': 'Language: {language}',
  'ds.empty.clearFilters': 'Clear filters',
  'ds.header.account': 'Account',
  /** The account menu's name below `sm`, where the language options live inside it. */
  'ds.header.accountMenu': 'Account and language',
  'ds.header.profile': 'Profile',
  'ds.header.signOut': 'Sign out',
  'ds.header.notificationsUnread': 'Notifications, {count} unread',
  'ds.header.accountNamed': 'Account: {name}',
  'ds.form.required': 'This field is required',
  'ds.form.invalidEmail': 'Enter a valid email',
  'ds.header.languageOptionCurrent': '{language} (current)',
  'ds.theme.light': 'Light',
  'ds.theme.dark': 'Dark',
  'ds.theme.system': 'System',
  'ds.theme.name': 'Theme: {current}. Switch to {next}.',
  'ds.theme.name.light': 'light',
  'ds.theme.name.dark': 'dark',
  'ds.theme.name.system': 'follow system',
  'ds.metric.trend.up': 'up',
  'ds.metric.trend.down': 'down',
  'ds.metric.trend.flat': 'flat',
  /* The phase dictionary the sample schedule is built from. It is here rather
     than in the consumer's catalogue because `BarChart` renders it and the
     library must be able to render on its own — a story has no consumer above
     it, so a key resolved from the application would draw the key itself. */
  'ds.phase.planning': 'Planning and pre-construction',
  'ds.phase.planning.short': 'Planning',
  'ds.phase.acquisition': 'Acquisition and mobilization',
  'ds.phase.acquisition.short': 'Acquisition',
  'ds.phase.demolition': 'Demolition and site prep',
  'ds.phase.demolition.short': 'Demolition',
  'ds.phase.foundation': 'Foundation',
  'ds.phase.foundation.short': 'Foundation',
  'ds.phase.framing': 'Framing and roofing',
  'ds.phase.framing.short': 'Framing',
  'ds.phase.utilities': 'Utilities and enclosure',
  'ds.phase.utilities.short': 'Utilities',
  'ds.phase.finishes': 'Finishes',
  'ds.phase.finishes.short': 'Finishes',
  'ds.phase.landscaping': 'Landscaping',
  'ds.phase.landscaping.short': 'Landscaping',
  'ds.phase.completion': 'Completion and sale',
  'ds.phase.completion.short': 'Completion',

  'ds.chart.series.budgeted': 'Budgeted',
  'ds.chart.series.actual': 'Actual',
  'ds.chart.bar.title': 'Budget by phase',
  'ds.chart.bar.description': 'Budgeted against actual cost for every funded phase.',
  'ds.chart.bar.summary': 'Budget for {phases} construction phases. Budgeted totals {budgeted}; actual totals {actual}.',
  'ds.chart.bar.chartLabel': 'Bar chart of budgeted against actual cost across {phases} phases',
  'ds.chart.bar.legend': 'Budget series',
  'ds.chart.bar.empty': 'All budget series are hidden. Switch one back on in the legend.',
  'ds.chart.bar.tableCaption': 'Budgeted against actual cost, by phase',
  'ds.chart.bar.columnPhase': 'Phase',
  'ds.chart.line.title': 'Spend by month',
  'ds.chart.line.description': 'Budgeted against actual, January to September 2026.',
  'ds.chart.line.summary': 'Monthly spend across {months} months. Budgeted totals {budgeted}; actual totals {actual}.',
  'ds.chart.line.chartLabel': 'Line chart of monthly spend, budgeted against actual, {months} months',
  'ds.chart.line.legend': 'Spend series',
  'ds.chart.line.empty': 'All spend series are hidden. Switch one back on in the legend.',
  'ds.chart.line.tableCaption': 'Spend by month, budgeted against actual',
  'ds.chart.line.columnMonth': 'Month',

  'ds.header.notifications': 'Notifications',

  /* ---------------------------------------------------------------------------
     PaginatedTable — its own chrome only. Column headers, filter labels and the
     empty/error state text arrive as props, so they are the caller's to
     translate and are absent here.
     ------------------------------------------------------------------------ */

  /** The paginator's four controls, which carry an icon and no text. */
  'ds.table.firstPage': 'First page',
  'ds.table.previousPage': 'Previous page',
  'ds.table.nextPage': 'Next page',
  'ds.table.lastPage': 'Last page',
  /** The footer's range summary. `count` is the row total and selects the plural. */
  'ds.table.displaying.one': 'Displaying {from}–{to} of {total} row',
  'ds.table.displaying.other': 'Displaying {from}–{to} of {total} rows',
  'ds.table.rowsPerPage': 'Rows per page',
  'ds.table.goToPage': 'Go to page',
  'ds.table.go': 'Go',
  /** `count` is the last page, which is also `{last}`. */
  'ds.table.page.one': 'Page {current} of {last}',
  'ds.table.page.other': 'Page {current} of {last}',
  /** `count` is the column total, which is also `{total}`. */
  'ds.table.columnsEnabled.one': '{visible} of {total} column enabled',
  'ds.table.columnsEnabled.other': '{visible} of {total} columns enabled',
  'ds.table.closePanel': 'Close panel',
  'ds.table.chooseColumns': 'Choose columns',
  'ds.table.columns': 'Columns',
  'ds.table.columnsPanelDescription': 'Choose which columns are shown. Your choice is remembered.',
  'ds.table.alwaysVisible': 'always visible',
  /** The filter trigger's whole accessible name, active state included. */
  'ds.table.filterColumn': 'Filter {column}',
  'ds.table.filterColumnActive': 'Filter {column} (filter active)',
  'ds.table.enterBothBounds': 'Enter both bounds.',
  'ds.table.clear': 'Clear',
  'ds.table.apply': 'Apply',
  'ds.table.from': 'From',
  'ds.table.to': 'To',
  'ds.table.fromDate': 'From date',
  'ds.table.toDate': 'To date',
  'ds.table.date': 'Date',
  'ds.table.time': 'Time',
  /** A format rather than a word: `parseTimeInput` reads `HH:MM` in every locale. */
  'ds.table.timePlaceholder': 'HH:MM',
  'ds.table.search': 'Search…',
  'ds.table.selectedValues': 'Selected values',
  'ds.table.removeValue': 'Remove {value}',
  /** `count` is the term length the source asks for before it will search. */
  'ds.table.minSearchLength.one': 'Type at least {count} character.',
  'ds.table.minSearchLength.other': 'Type at least {count} characters.',
  'ds.table.searching': 'Searching…',
  'ds.table.noMatches': 'No matches.',
  'ds.table.refineSearch': 'Refine your search — more matches exist.',
  'ds.table.noSearchSource': 'No search source is configured for this column.',
  /** The active-filter summary. The column names follow it as plain text. */
  'ds.table.filters': 'Filters:',
  'ds.table.clearAllFilters': 'Clear all filters',
  'ds.table.scrollForMoreColumns': 'Scroll for more columns',
  /** Read before a sortable column's header, which is appended after it. */
  'ds.table.sortBy': 'Sort by',
  'ds.table.subtotal': 'Subtotal',
  /** A step's position, spoken rather than drawn: "Step 2 of 4". */
  'ds.stepper.position': 'Step {current} of {total}',
  /** The breadcrumb landmark's accessible name, when a caller names none. */
  'ds.breadcrumb.label': 'Breadcrumb',
  'ds.alert.dismiss': 'Dismiss',
  'ds.toast.region': 'Notifications',
  'ds.toast.dismiss': 'Dismiss notification',
  /** Aggregation captions in the subtotal row, abbreviated for the label's size. */
  'ds.table.aggSum': 'Sum',
  'ds.table.aggAvg': 'Avg',
  'ds.table.aggMin': 'Min',
  'ds.table.aggMax': 'Max',
  'ds.table.aggCount': 'Count',
  'ds.table.aggDistinct': 'Distinct',
  'ds.table.aggMedian': 'Median',
  'ds.table.aggStddev': 'Std dev',
  /** The one polite live region every table state change is announced from. */
  'ds.table.announceLoading': 'Loading rows.',
  'ds.table.announceError': 'Error. {message}',
  'ds.table.announceNoRowsFiltered': 'No rows match the active filters.',
  'ds.table.announceNoRows': 'No rows.',
  /** `count` is the row total and selects the plural; `{shown}` is on screen. */
  'ds.table.announceDisplaying.one': 'Displaying {shown} of {total} row.',
  'ds.table.announceDisplaying.other': 'Displaying {shown} of {total} rows.',
  /*
     Filter validation. `buildFilter` runs at module scope, where the translator
     is unreachable, so it returns one of these KEYS and the table resolves it at
     the render site that displays it.
  */
  'ds.table.errorBothBounds': 'Enter both bounds to apply this filter.',
  'ds.table.errorStartDate': 'Enter a valid start date.',
  'ds.table.errorEndDate': 'Enter a valid end date.',
  'ds.table.errorStartBeforeEnd': 'The start date must not be after the end date.',
  'ds.table.errorStartTime': 'Enter a valid start time (HH:MM).',
  'ds.table.errorEndTime': 'Enter a valid end time (HH:MM).',
  'ds.table.errorStartAfterEnd': 'The start must not be after the end.',
  'ds.table.errorLowerBound': 'Enter a valid lower bound.',
  'ds.table.errorUpperBound': 'Enter a valid upper bound.',
  'ds.table.errorPercentageRange': 'Enter a percentage between 0 and 100.',
  'ds.table.errorBoundsOrder': 'The lower bound must not be greater than the upper bound.',
  'ds.table.errorValidDate': 'Enter a valid date.',
  'ds.table.errorValidTime': 'Enter a valid time (HH:MM).',
  'ds.table.errorValidNumber': 'Enter a valid number.',
  'ds.table.errorValidValue': 'Enter a valid value.',

  /* ---------------------------------------------------------------------------
     NotificationsList — the panel's own headings and states. A notification's
     title, description and timestamp are data and stay where they are.
     ------------------------------------------------------------------------ */
  'ds.notifications.unread': 'Unread',
  /** `count` is the unread total. `en` reads the same in both forms; `pt-BR` does not. */
  'ds.notifications.unreadCount.one': '{count} unread',
  'ds.notifications.unreadCount.other': '{count} unread',
  'ds.notifications.noneUnread': 'No unread notifications',
  'ds.notifications.markAllRead': 'Mark all as read',
  'ds.notifications.errorTitle': 'Could not load notifications',
  'ds.notifications.emptyTitle': 'No notifications',
  'ds.notifications.emptyDescription': 'Project updates, approvals and alerts land here.',

  /* ---------------------------------------------------------------------------
     InputNumber
     ------------------------------------------------------------------------ */
  'ds.table.errorFallback': 'The data source failed to respond.',
  'ds.number.decrease': 'Decrease',
  'ds.number.increase': 'Increase',
  'ds.number.saved': 'Saved!',

  /* ---------------------------------------------------------------------------
     ChartFrame, Select, PdfPreview, Link
     ---------------------------------------------------------------------------
     The chrome these four carry was left as literals when the rest of the
     library moved into the catalogue, so it stayed English under a Portuguese
     page and no language change could reach it — the one class of string a
     selector genuinely cannot fix.
     ------------------------------------------------------------------------ */
  'ds.chart.dataTable': 'Data table',

  'ds.select.searching': 'Searching…',
  'ds.select.noMatches': 'No matches found.',
  'ds.select.noMatchesFor': 'No matches for “{query}”.',
  'ds.select.truncatedHint': 'Showing the closest matches — keep typing to narrow the list.',
  'ds.select.minLength.one': 'Type {count} more character to search.',
  'ds.select.minLength.other': 'Type {count} more characters to search.',

  'ds.pdf.zoomIn': 'Zoom in',
  'ds.pdf.zoomOut': 'Zoom out',
  'ds.pdf.resetZoom': 'Reset zoom to 100%',
  'ds.pdf.previousPage': 'Previous page',
  'ds.pdf.nextPage': 'Next page',
  'ds.pdf.pageOf': 'Page {current} of {total}',
  'ds.pdf.loading': 'Loading document…',
  'ds.pdf.loadingLabel': 'Loading document',
  'ds.pdf.errorTitle': 'The document did not load',

  'ds.link.newTab': '(opens in a new tab)',
  'ds.search.clear': 'Clear search',
  'ds.search.label': 'Search',
} satisfies Record<string, string>;

/** Every key the design system's own chrome can ask for. */
export type DsTranslationKey = keyof typeof en;
